import { safeEmailHref, safeImageSrc } from './sanitize';

export type Block =
  | { id: string; type: 'heading'; level: 1 | 2 | 3; text: string; align: 'left' | 'center' }
  | { id: string; type: 'paragraph'; text: string; align: 'left' | 'center' }
  | { id: string; type: 'image'; url: string; alt: string; width: number; align: 'left' | 'center' | 'right'; href?: string; bg?: 'none' | 'white' }
  | { id: string; type: 'button'; text: string; href: string; align: 'left' | 'center' }
  | { id: string; type: 'youtube'; videoUrl: string; title: string; thumbnailUrl?: string; align: 'left' | 'center' };

const FONT = `system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif`;

// Optional rewriter for click-tracking. Returns the URL to put in the <a>
// (typically a /api/track/click URL). Receives only schemes that already
// passed safeEmailHref; mailto:/tel: are passed through unrewritten.
export type LinkRewriter = (url: string) => Promise<string>;

export async function blocksToHtml(blocks: Block[], rewrite?: LinkRewriter): Promise<string> {
  const out: string[] = [];
  for (const b of blocks) out.push(await blockToHtml(b, rewrite));
  return out.join('\n');
}

async function maybeRewrite(href: string, rewrite?: LinkRewriter): Promise<string> {
  if (!rewrite) return href;
  // Don't wrap mailto:/tel: — only http(s) get tracked.
  try {
    const proto = new URL(href).protocol;
    if (proto !== 'http:' && proto !== 'https:') return href;
  } catch {
    return href;
  }
  return rewrite(href);
}

async function blockToHtml(block: Block, rewrite?: LinkRewriter): Promise<string> {
  switch (block.type) {
    case 'heading': {
      const sizes: Record<1 | 2 | 3, string> = { 1: '28px', 2: '22px', 3: '18px' };
      return `<h${block.level} style="margin:0 0 12px;font:700 ${sizes[block.level]}/1.25 ${FONT};color:#f8fafc;text-align:${block.align};">${esc(block.text)}</h${block.level}>`;
    }
    case 'paragraph': {
      const html = esc(block.text).replace(/\n/g, '<br>');
      return `<p style="margin:0 0 16px;color:#cbd5e1;font:400 16px/1.6 ${FONT};text-align:${block.align};">${html}</p>`;
    }
    case 'image': {
      const w = Math.max(50, Math.min(560, block.width || 400));
      const useWhite = block.bg === 'white';
      const radius = useWhite ? 'border-radius:6px;' : '';
      const imgStyle = `display:inline-block;max-width:${w}px;width:100%;height:auto;border:0;${radius}`;
      const safeSrc = safeImageSrc(block.url);
      const img = `<img src="${esc(safeSrc)}" alt="${esc(block.alt)}" width="${w}" style="${imgStyle}">`;
      const safeHref = block.href ? safeEmailHref(block.href) : '';
      const finalHref = safeHref ? await maybeRewrite(safeHref, rewrite) : '';
      const linked = finalHref
        ? `<a href="${esc(finalHref)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;">${img}</a>`
        : img;
      const wrapped = useWhite
        ? `<span style="display:inline-block;background:#ffffff;padding:12px;border-radius:12px;line-height:0;">${linked}</span>`
        : linked;
      return `<div style="margin:24px 0;text-align:${block.align};">${wrapped}</div>`;
    }
    case 'button': {
      const align = block.align;
      const safeHref = safeEmailHref(block.href);
      const finalHref = safeHref ? await maybeRewrite(safeHref, rewrite) : '';
      // Unsafe scheme: render as inert text-only button so the layout is
      // preserved but the malicious URL isn't an executable link.
      if (!finalHref) {
        return `<div style="margin:24px 0;text-align:${align};">
          <span style="display:inline-block;background:#0ea5e9;color:#ffffff;font:700 16px/48px ${FONT};padding:0 28px;border-radius:10px;">${esc(block.text)}</span>
        </div>`;
      }
      return `<div style="margin:24px 0;text-align:${align};">
        <a href="${esc(finalHref)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;background:#0ea5e9;color:#ffffff;text-decoration:none;font:700 16px/48px ${FONT};padding:0 28px;border-radius:10px;box-shadow:0 4px 12px rgba(14,165,233,0.3);">${esc(block.text)}</a>
      </div>`;
    }
    case 'youtube': {
      const safeHref = safeEmailHref(block.videoUrl);
      const finalHref = safeHref ? await maybeRewrite(safeHref, rewrite) : '';
      if (!finalHref) return '';
      const thumbnail = safeImageSrc(block.thumbnailUrl || deriveYoutubeThumbnail(block.videoUrl));
      const title = esc(block.title || 'Watch video');
      return `<div style="margin:24px 0;text-align:${block.align};">
        <a href="${esc(finalHref)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:inline-block;max-width:560px;width:100%;">
          <img src="${esc(thumbnail)}" alt="${title}" width="560" style="display:block;width:100%;height:auto;border:0;border-radius:10px;">
        </a>
        <p style="margin:10px 0 0;color:#cbd5e1;font:600 15px/1.4 ${FONT};text-align:${block.align};">${title}</p>
      </div>`;
    }
  }
}

const HTML_ESC: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
};
function esc(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => HTML_ESC[c]);
}

export function newBlockId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function defaultBlock(type: Block['type']): Block {
  switch (type) {
    case 'heading':
      return { id: newBlockId(), type, level: 2, text: 'Your heading', align: 'left' };
    case 'paragraph':
      return { id: newBlockId(), type, text: 'Write your paragraph here. Press Enter for line breaks.', align: 'left' };
    case 'image':
      return { id: newBlockId(), type, url: '', alt: '', width: 400, align: 'center', bg: 'none' };
    case 'button':
      return { id: newBlockId(), type, text: 'Check it out', href: 'https://atomic-rose.com', align: 'center' };
    case 'youtube':
      return {
        id: newBlockId(),
        type,
        videoUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        title: 'Watch the release demo',
        align: 'center',
      };
  }
}

function deriveYoutubeThumbnail(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace('/', '').trim();
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v') || '';
      if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    }
  } catch {
    // fall through
  }
  return '';
}
